import { useVoiceProfile, useVoiceHistory } from "../../hooks/useVoice";
import { LoadErrorState } from "../ui/LoadState";
import { isNotFoundError } from "../../lib/load-state";
import type { VoiceKey, VoicePatch } from "../../../../shared/types";

interface VoiceSectionProps {
  brandId: string;
  voiceKey: VoiceKey;
  label: string;
}

const PATCH_TYPE_LABELS: Record<VoicePatch["patch_type"], string> = {
  tone: "Tone",
  tone_descriptors_add: "Tone +",
  tone_descriptors_remove: "Tone −",
  vocabulary_add: "Vocab +",
  vocabulary_remove: "Vocab −",
  guidelines: "Guidelines",
  example_add: "Example",
  example_remove: "Example −",
};

const PATCH_TYPE_COLORS: Record<VoicePatch["patch_type"], string> = {
  tone: "bg-blue-100 text-blue-800",
  tone_descriptors_add: "bg-indigo-100 text-indigo-800",
  tone_descriptors_remove: "bg-rose-100 text-rose-800",
  vocabulary_add: "bg-green-100 text-green-800",
  vocabulary_remove: "bg-red-100 text-red-800",
  guidelines: "bg-purple-100 text-purple-800",
  example_add: "bg-amber-100 text-amber-800",
  example_remove: "bg-orange-100 text-orange-800",
};

const VISIBLE_PATCH_LIMIT = 8;

function patchSourceLabel(patch: VoicePatch): string {
  if (patch.source === "agent_workbench") return "Workbench";
  if (patch.source === "agent_chat") return "Agent chat";
  if (patch.source === "human") return "Human";
  return "Unknown source";
}

function VoiceSection({ brandId, voiceKey, label }: VoiceSectionProps) {
  const profileQ = useVoiceProfile(brandId, voiceKey);
  const historyQ = useVoiceHistory(brandId, voiceKey);
  const { data: profileRes, isLoading: profileLoading } = profileQ;
  const { data: historyRes, isLoading: historyLoading } = historyQ;
  // A 404 means "no profile yet"; any other failure leaves the profile unknown.
  const profileFailed = profileQ.isError && !profileRes && !isNotFoundError(profileQ.error);
  const historyFailed = historyQ.isError && !historyRes && !isNotFoundError(historyQ.error);

  const profile = profileRes?.data ?? null;
  const patches = historyRes?.data ?? [];
  const visiblePatches = [...patches].reverse().slice(0, VISIBLE_PATCH_LIMIT);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{label}</h3>

      {profileLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          Loading profile...
        </div>
      ) : profileFailed ? (
        <LoadErrorState compact title="Voice profile could not be loaded" onRetry={() => void profileQ.refetch()} />
      ) : !profile ? (
        <p className="text-sm text-slate-500">No voice profile yet. Corrections will appear here once the agent learns your preferences.</p>
      ) : (
        <div className="mb-4 space-y-2">
          {profile.tone && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Tone</span>
              <p className="mt-0.5 text-sm text-slate-800">{profile.tone}</p>
            </div>
          )}
          {profile.vocabulary.length > 0 && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Vocabulary</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {profile.vocabulary.map((word) => (
                  <span
                    key={word}
                    className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700"
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>
          )}
          {profile.guidelines && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Guidelines</span>
              <p className="mt-0.5 text-sm text-slate-800 whitespace-pre-wrap">{profile.guidelines}</p>
            </div>
          )}
          {profile.example_posts.length > 0 && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Example posts</span>
              <ul className="mt-1 space-y-1">
                {profile.example_posts.map((post, i) => (
                  <li key={i} className="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700 italic">
                    {post}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div>
        <h4 className="mb-2 text-xs font-medium text-slate-500 uppercase tracking-wide">Patch history</h4>
        {patches.length > VISIBLE_PATCH_LIMIT ? (
          <p className="mb-2 text-xs text-slate-500">
            Showing the most recent {VISIBLE_PATCH_LIMIT} patches.
          </p>
        ) : null}
        {historyLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            Loading history...
          </div>
        ) : historyFailed ? (
          <LoadErrorState compact title="Patch history could not be loaded" onRetry={() => void historyQ.refetch()} />
        ) : patches.length === 0 ? (
          <p className="text-sm text-slate-400">No patches applied yet.</p>
        ) : (
          <ul className="space-y-2">
            {visiblePatches.map((patch, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span
                  className={`mt-0.5 inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-xs font-medium ${PATCH_TYPE_COLORS[patch.patch_type]}`}
                >
                  {PATCH_TYPE_LABELS[patch.patch_type]}
                </span>
                <span className="flex-1 text-slate-700">
                  <span className="block">{patch.value}</span>
                  <span className="mt-1 block text-xs text-slate-400">{patchSourceLabel(patch)}</span>
                </span>
                <span className="shrink-0 text-xs text-slate-400">
                  {new Date(patch.applied_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface VoicePatchHistoryProps {
  brandId: string;
  brandName?: string;
}

export function VoicePatchHistory({ brandId, brandName }: VoicePatchHistoryProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Voice profiles are learned from your corrections during content review. The agent updates these automatically when you give feedback.
      </p>
      <VoiceSection
        brandId={brandId}
        voiceKey="brand"
        label={`${brandName ?? "Brand"} Voice`}
      />
      <VoiceSection
        brandId={brandId}
        voiceKey="founder_personal"
        label="Founder personal voice"
      />
    </div>
  );
}
